/**
 * Cambiar la dirección de véltica del sitio.
 *
 * Hasta ahora se adivinaba del nombre del proyecto al publicar y no había forma
 * de tocarla: el nombre y la dirección eran la misma cosa, así que renombrar el
 * proyecto movía el sitio de sitio y no renombrarlo lo dejaba en una dirección
 * que ya no se parecía a nada.
 *
 * ── Se comprueba mientras se escribe ────────────────────────────────────────
 * Y no sólo al guardar. Una dirección es de quien la pide primero, así que
 * enterarse de que está ocupada después de darle a guardar es enterarse tarde:
 * ya se pensó el nombre, ya se escribió entero. El servidor contesta las dos
 * cosas —si la forma vale y si está libre— porque las dos son suyas: el panel
 * no puede saber qué hay en las tablas de otras cuentas.
 *
 * ── El cambio no es instantáneo, y se dice ──────────────────────────────────
 * Cambiar la dirección mueve el sitio entero de prefijo en R2, objeto a objeto,
 * y tira la entrada en caché de la anterior. En un sitio con muchas páginas eso
 * son un par de minutos en los que la dirección nueva contesta 404. Antes
 * eso pasaba a escondidas: el panel decía «Dirección guardada» y quien iba a
 * comprobarlo se encontraba una página de error y creía que había roto su sitio.
 *
 * Ahora la fila lo cuenta mientras dura, y el final no es un temporizador
 * inventado: se le pregunta al servidor si la dirección ya sirve —la misma
 * lectura que hace una visita— hasta que contesta que sí.
 */
import { useEffect, useState } from 'react'
import { IconAlertCircle, IconCheck, IconExternalLink, IconLoader2, IconPencil } from '@tabler/icons-react'
import { getSiteSubdomain, getSiteSubdomainStatus, saveSiteSubdomain } from '../../../lib/api'
import { useToast } from '../../Toast'
import { useI18n } from '../../../lib/i18n'
import { useDebounced } from '../Pagination'

/**
 * El cambio, mientras está pasando.
 *
 * `moving` es el servidor copiando los archivos; `settling`, el rato entre que
 * contesta y que la dirección nueva empieza a abrir de verdad. Se cuentan igual
 * en pantalla —quien mira no distingue una cosa de la otra, ni le importa— pero
 * se separan aquí porque sólo la segunda se puede preguntar.
 */
type Applying =
  | { phase: 'moving' | 'settling'; hostname: string }
  | { phase: 'live' }
  | { phase: 'slow' }

/** Cada cuánto se pregunta si la dirección nueva ya sirve. */
const SETTLE_POLL_MS = 3000
/**
 * Cuánto se pregunta antes de dejar de hacerlo.
 *
 * Cinco minutos. Estaba en dos, calculado como «el doble de lo que tarda un
 * sitio grande» sobre la suposición de que eso era cerca de un minuto — y en
 * producción se han medido cambios que tardan los dos minutos enteros. Con el
 * plazo justo encima de lo que tarda de verdad, el caso NORMAL se salía por la
 * puerta de «no responde»: el sondeo se rendía justo antes de que la dirección
 * empezara a abrir, y lo que se leía era un fallo que no había ocurrido.
 *
 * Pasado el plazo, seguir girando indefinidamente sería fingir que todavía se
 * sabe algo: se dice que no ha respondido y se deja de mirar.
 */
const SETTLE_LIMIT_MS = 300_000
/** Lo que la confirmación en verde se queda antes de apagarse sola. */
const LIVE_MS = 6000

export function SubdomainField({ projectId, token, current, publicDomain, href, onSaved }: {
  projectId: string
  token: string
  /** Lo que se enseña cuando no se está editando. */
  current: string
  publicDomain: string
  /** Si el sitio está publicado, a dónde lleva el nombre. */
  href?: string | undefined
  onSaved: (subdomain: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(current)
  const [saving, setSaving] = useState(false)
  const [applying, setApplying] = useState<Applying | null>(null)
  const [check, setCheck] = useState<{ available: boolean; reason: string | null } | null>(null)
  const [checking, setChecking] = useState(false)
  const debounced = useDebounced(value)
  const { showToast } = useToast()
  const { t } = useI18n()

  useEffect(() => { if (!editing) setValue(current) }, [current, editing])

  useEffect(() => {
    const candidate = debounced.trim().toLowerCase()
    if (!editing || !candidate || candidate === current) { setCheck(null); return }
    let alive = true
    setChecking(true)
    getSiteSubdomain(token, projectId, candidate)
      .then((data) => {
        /* Puede haber llegado la respuesta de una escritura anterior: se
           descarta si ya no es lo que hay en el campo. */
        if (!alive || !data.check || data.check.value !== candidate) return
        /* El código en el idioma del panel; la frase del servidor sólo si el
           panel todavía no conoce ese código. */
        const code = data.check.code
        const translated = code ? t(`error.${code}`) : ''
        setCheck({ available: data.check.available, reason: code && translated !== `error.${code}` ? translated : data.check.reason })
      })
      .catch(() => { if (alive) setCheck(null) })
      .finally(() => { if (alive) setChecking(false) })
    return () => { alive = false }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced, editing, current, projectId, token])

  /* Preguntar hasta que la dirección nueva sirva. Un fallo suelto de red no es
     un veredicto —se vuelve a preguntar—, y sólo el plazo agotado cierra la
     espera. */
  const settlingHost = applying?.phase === 'settling' ? applying.hostname : null
  useEffect(() => {
    if (!settlingHost) return
    let alive = true
    let timer = 0
    const deadline = Date.now() + SETTLE_LIMIT_MS
    const ask = async () => {
      let serving = false
      try {
        const status = await getSiteSubdomainStatus(token, projectId)
        serving = status.subdomain === settlingHost && status.serving
      } catch { /* Se reintenta: la siguiente vuelta lo dirá. */ }
      if (!alive) return
      if (serving) { setApplying({ phase: 'live' }); return }
      if (Date.now() >= deadline) { setApplying({ phase: 'slow' }); return }
      timer = window.setTimeout(() => { void ask() }, SETTLE_POLL_MS)
    }
    void ask()
    return () => { alive = false; window.clearTimeout(timer) }
  }, [settlingHost, token, projectId])

  /* La confirmación se apaga sola: es un acuse de recibo, no un estado. */
  const live = applying?.phase === 'live'
  useEffect(() => {
    if (!live) return
    const timer = window.setTimeout(() => setApplying(null), LIVE_MS)
    return () => window.clearTimeout(timer)
  }, [live])

  const candidate = value.trim().toLowerCase()
  const unchanged = candidate === current
  const canSave = Boolean(candidate) && !unchanged && check?.available === true && !saving

  const save = async () => {
    if (!canSave) return
    setSaving(true)
    /* El formulario se cierra YA, sin esperar la respuesta: mover el sitio de
       prefijo puede tardar cerca de un minuto, y un formulario congelado en
       «Guardando…» todo ese rato no dice qué está pasando. Lo que queda se
       cuenta en la fila, que es donde está la dirección. */
    setEditing(false)
    setApplying({ phase: 'moving', hostname: candidate })
    try {
      const result = await saveSiteSubdomain(token, projectId, candidate)
      onSaved(result.subdomain)
      /* «Movido» es que el sitio estaba publicado y sus archivos cambiaron de
         prefijo: hasta que el borde se entera, la dirección nueva contesta 404.
         Sin mover no hay nada que esperar y se termina aquí. */
      if (result.moved) {
        setApplying({ phase: 'settling', hostname: result.subdomain })
      } else {
        setApplying(null)
        showToast(t('subdomain.saved'))
      }
    } catch (error) {
      /* De vuelta al formulario con lo que se había escrito: cerrarlo fue una
         apuesta a que saldría bien, y perder el nombre pensado sería cobrarle
         al cliente esa apuesta. */
      setApplying(null)
      setEditing(true)
      setValue(candidate)
      showToast(error instanceof Error ? error.message.replace(/^\d+:\s*/, '') : t('subdomain.saveFailed'), 'error')
    } finally {
      setSaving(false)
    }
  }

  /* Sin editar, la dirección misma: el nombre y, a su derecha, «Cambiar».
     Al pulsarlo el nombre SE CONVIERTE en el campo, en su sitio. Antes el botón
     colgaba de un pie aparte y el formulario se abría debajo, así que lo que se
     estaba cambiando y dónde se escribía eran dos renglones distintos. */
  if (!editing) {
    const working = applying?.phase === 'moving' || applying?.phase === 'settling'
    return (
      <>
        <div className="dash-domain-card-line">
          {href ? (
            <a className="dash-domain-card-name" href={href} target="_blank" rel="noreferrer">
              {current}.{publicDomain} <IconExternalLink size={13} aria-hidden="true" />
            </a>
          ) : (
            <span className="dash-domain-card-name">{current}.{publicDomain}</span>
          )}
          {working || applying?.phase === 'live' ? (
            /* En el sitio del botón, no debajo: es lo que ha pasado con la
               acción que se pulsó ahí. */
            <span className={`dash-domain-applying${applying?.phase === 'live' ? ' is-live' : ''}`} role="status">
              {applying?.phase === 'live'
                ? <><IconCheck size={13} aria-hidden="true" /> {t('subdomain.live')}</>
                : <><IconLoader2 size={13} className="dash-spin" aria-hidden="true" /> {t('subdomain.applying')}</>}
            </span>
          ) : (
            /* Con marco y con lápiz. Como texto suelto se perdía junto al
               nombre —que va en negro y con peso— y era justo la única acción
               de esta pantalla que casi nadie encontraba. */
            <button
              className="dash-secondary-btn dash-domain-card-change-address"
              type="button"
              onClick={() => { setApplying(null); setEditing(true); setValue(current); setCheck(null) }}
            >
              <IconPencil size={13} aria-hidden="true" /> {t('subdomain.change')}
            </button>
          )}
        </div>

        {working ? (
          <p className="dash-subdomain-hint">
            {/* Partido porque el nombre nuevo va en negrita en medio. */}
            {t('subdomain.movingTo')} <strong>{applying.hostname}.{publicDomain}</strong>{t('subdomain.movingBody')}
          </p>
        ) : applying?.phase === 'slow' ? (
          <p className="dash-subdomain-hint">
            {t('subdomain.slow')}
          </p>
        ) : null}
      </>
    )
  }

  return (
    <div className="dash-subdomain-form">
      <div className="dash-subdomain-input">
        <input
          value={value}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          aria-label={t('subdomain.aria')}
          /* Se normaliza al escribir, no al guardar: si se acepta «Mi Tienda» y
             se guarda «mi-tienda», lo que se ve y lo que se guarda difieren. */
          onChange={(event) => setValue(event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && canSave) void save()
            if (event.key === 'Escape') { setEditing(false); setValue(current) }
          }}
        />
        <span className="dash-subdomain-suffix">.{publicDomain}</span>
      </div>

      <div className="dash-subdomain-actions">
        <button className="dash-domain-card-change" type="button" disabled={saving} onClick={() => { setEditing(false); setValue(current) }}>
          {t('common.cancel')}
        </button>
        <button className="dash-secondary-btn" type="button" disabled={!canSave} onClick={() => void save()}>
          <IconCheck size={14} /> {t(saving ? 'subdomain.saving' : 'subdomain.save')}
        </button>
      </div>

      {/* Nada mientras se comprueba o si no ha cambiado: un aviso que aparece y
          desaparece a cada tecla se lee como un error intermitente. */}
      {unchanged || checking || !check ? (
        <p className="dash-subdomain-hint">{t('subdomain.rules')}</p>
      ) : check.available ? (
        <p className="dash-subdomain-hint is-ok"><IconCheck size={13} /> {t('subdomain.free', { host: `${candidate}.${publicDomain}` })}</p>
      ) : (
        <p className="dash-subdomain-hint is-error"><IconAlertCircle size={13} /> {check.reason}</p>
      )}
    </div>
  )
}
